import * as Yup from "yup";

export const dishValidationSchema = Yup.object().shape({
  name: Yup.string().required('Dish Name is required').max(18, 'Dish Name must not exceed 18 characters'),
  food_type: Yup.string().required('Food Type is required'),
  schedule_enabled: Yup.boolean().optional(),
  sessions: Yup.array().of(
    Yup.object().shape({
      day_from: Yup.number().min(1).max(7).required("Day From is required"),
      day_to: Yup.number().min(1).max(7).required("Day To is required"),
      start_hhmm: Yup.number().min(0).max(2359).required("Start HHMM is required"),
      end_hhmm: Yup.number().min(0).max(2359).required("End HHMM is required"),
      label: Yup.string().optional(),
      status: Yup.boolean().optional(),
    }).test(
      "day-order",
      "End day must be the same as or after start day (Mon → Sun)",
      (session) => {
        if (!session) return true;
        return Number(session.day_to) >= Number(session.day_from);
      },
    ).test(
      "same-day-end-after-start",
      "For same-day sessions, end time must be after start time",
      (session) => {
        if (!session) return true;
        const sameDay = Number(session.day_from) === Number(session.day_to);
        if (!sameDay) return true;
        return Number(session.end_hhmm) > Number(session.start_hhmm);
      },
    ).test(
      "long-range-no-overnight-clock",
      "For day ranges of 6+ days, end time must be after start time on the clock",
      (session) => {
        if (!session) return true;
        const from = Number(session.day_from);
        const to = Number(session.day_to);
        if (to < from) return true;
        const span = to - from + 1;
        if (span < 6) return true;
        return Number(session.end_hhmm) > Number(session.start_hhmm);
      },
    )
  )
    .when("schedule_enabled", {
      is: true,
      then: (schema) => schema.min(1, "At least one session is required when schedule is enabled"),
      otherwise: (schema) => schema.optional(),
    })
    .optional(),
  icon: Yup.mixed<File[]>().test(
    'fileSize',
    'File size too large, maximum is 2MB',
    (value) => {
      if (!value || value.length === 0) return true;
      if (typeof value[0] === "string") return true;
      if (Array.isArray(value)) {
        const fileDetails = value[0];
        return fileDetails && fileDetails.size <= 2 * 1024 * 1024;
      } else {
        return value && value['size'] <= 2 * 1024 * 1024;
      }
    }
  )
  .test(
    'fileFormat',
    'Unsupported format, must be JPEG,WEBP, PNG',
    (value) => {
      if (!value || value.length === 0) return true;
      if (typeof value[0] === "string") return true;
      if (Array.isArray(value)) {
        const fileDetails = value[0];
        if (fileDetails) {
          return ['image/jpeg', 'image/png', 'image/webp'].includes(fileDetails.type);
        }
        return false;
      } else {
        return ['image/jpeg', 'image/png', 'image/webp'].includes(value['type']);
      }

    }
  ).required('Please upload Icon image.'),
});

export const bannerValidationSchema = Yup.object().shape({
  image_url: Yup.mixed<File[]>().test(
    'fileSize',
    'File size too large, maximum is 2MB',
    (value) => {
      if (!value || value.length === 0) return true;
      if (typeof value[0] === "string") return true;
      if (Array.isArray(value)) {
        const fileDetails = value[0];
        return fileDetails && fileDetails.size <= 2 * 1024 * 1024;
      } else {
        return value && value['size'] <= 2 * 1024 * 1024;
      }
    }
  )
  .test(
    'fileFormat',
    'Unsupported format, must be JPEG,WEBP, PNG',
    (value) => {
      if (!value || value.length === 0) return true;
      if (typeof value[0] === "string") return true;
      if (Array.isArray(value)) {
        const fileDetails = value[0];
        if (fileDetails) {
          return ['image/jpeg', 'image/png', 'image/webp'].includes(fileDetails.type);
        }
        return false;
      } else {
        return ['image/jpeg', 'image/png', 'image/webp'].includes(value['type']);
      }
    }
  ).required('Please upload Banner image.'),
  // nullable: fields left empty come back from the API as null.
  title: Yup.string().nullable().optional().max(30, 'Title must not exceed 30 characters'),
  subtitle: Yup.string().nullable().optional().max(100, 'Subtitle must not exceed 100 characters'),
  cta_button: Yup.string().nullable().optional().max(18, 'CTA Button must not exceed 18 characters'),
  // background_color: Yup.string().required('Background Color is required'),
  promotion_type: Yup.string().required('Promotion Type is required'),
  promotion_link: Yup.string()
    .nullable()
    .when('promotion_type', {
      is: 'organization',
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required('Promotion Link is required'),
    })
    .when('promotion_type', {
      is: 'url',
      then: (schema) => schema.url('Enter a valid URL (https://...)'),
    }),
  placement: Yup.string().optional(),
  schedule_enabled: Yup.boolean().optional(),
  sessions: Yup.array().of(
    Yup.object().shape({
      day_from: Yup.number().min(1).max(7).required("Day From is required"),
      day_to: Yup.number().min(1).max(7).required("Day To is required"),
      start_hhmm: Yup.number().min(0).max(2359).required("Start HHMM is required"),
      end_hhmm: Yup.number().min(0).max(2359).required("End HHMM is required"),
      label: Yup.string().optional(),
      status: Yup.boolean().optional(),
    }),
  )
    .when("schedule_enabled", {
      is: true,
      then: (schema) => schema.min(1, "At least one session is required when schedule is enabled"),
      otherwise: (schema) => schema.optional(),
    })
    .optional(),
  // sequence: Yup.number().min(1, 'Sequence must be greater than 0').required('Sequence is required'),
  status: Yup.boolean().optional(),
});

// System Announcement Validation Schema
export const systemAnnouncementValidationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Title is required')
    .max(100, 'Title must not exceed 100 characters'),
  message: Yup.string()
    .required('Message is required')
    .max(500, 'Message must not exceed 500 characters'),
  type: Yup.string().oneOf(['system'], 'Type must be system').required(),
  image_url: Yup.string()
    .url('Image URL must be a valid URL')
    .required('Image URL is required'),
  data: Yup.object().shape({
    maintenance_date: Yup.string()
      .required('Maintenance date is required')
      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
    start_time: Yup.string()
      .required('Start time is required')
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format (24-hour)'),
    end_time: Yup.string()
      .required('End time is required')
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:MM format (24-hour)'),
  }).required('Data is required'),
});

// Promotional Announcement Validation Schema
export const promotionalAnnouncementValidationSchema = Yup.object().shape({
  title: Yup.string()
    .required('Title is required')
    .max(100, 'Title must not exceed 100 characters'),
  message: Yup.string()
    .required('Message is required')
    .max(500, 'Message must not exceed 500 characters'),
  type: Yup.string().oneOf(['promotion'], 'Type must be promotion').required(),
  image_url: Yup.string()
    .url('Image URL must be a valid URL')
    .optional()
    .nullable(),
  data: Yup.object().shape({
    coupon_code: Yup.string()
      .max(50, 'Coupon code must not exceed 50 characters')
      .optional(),
    discount: Yup.mixed()
      .optional()
      .nullable()
      .test('is-number', 'Discount must be a number', (value) => {
        if (value === undefined || value === null || value === '') {
          return true; // Optional, skip validation if empty
        }
        const num = Number(value);
        return !isNaN(num);
      })
      .test('is-valid-range', 'Discount must be between 0 and 100', (value) => {
        if (value === undefined || value === null || value === '') {
          return true; // Skip range check if empty
        }
        const num = Number(value);
        return num >= 0 && num <= 100;
      })
      .transform((value) => {
        // Convert to number for submission
        if (value === '' || value === undefined || value === null) {
          return undefined;
        }
        return Number(value);
      }),
    url: Yup.string()
      .url('URL must be a valid URL')
      .optional()
      .nullable(),
  }).optional(),
});